// feat/buyer-cart-ui (2026-05-26) — 외부 바이어 컨테이너 혼적 장바구니 UI
// 백엔드: /api/buyer-app/carts (C6 — eligible/list/detail/add/remove/lock). 자체완결 1페이지.
//   상단: 내 컨테이너 카트(국가별, 적재율 바 + 품목 + 빼기/잠금) / 하단: 담을 수 있는 차량(담기).
// ⚠️ lock 후 = 'locked' (CART-D 입금트리거/일정 미구현) → 문구는 "결제·배정 대기" (에스크로/선적 시작 약속 금지).

import React, { useState, useEffect, useCallback } from 'react';
import { API } from '../../utils/api';
import { authBuyerFetch, isUnauthorized, handleUnauthorized, displayApiError } from './buyerApi';
import { useNavigate } from 'react-router-dom';
import AuctionBuyerLayout from './AuctionBuyerLayout';
import EmptyState from './EmptyState';
import { showToast } from './Toast';
import { confirm } from './ConfirmDialog';
import { useT } from '../../i18n/I18nProvider';

// 카트 상태 → i18n 키 (locked = 결제·배정 대기, CART-D 미구현이라 일정/에스크로 약속 금지)
var CART_STATUS_I18N = { draft: 'cartStatusDraft', locked: 'cartStatusLocked', assigned: 'cartStatusAssigned' };
var CART_STATUS_COLOR = { draft: '#64748b', locked: '#0ea5e9', assigned: '#10b981' };
// 백엔드 에러 코드 → i18n 키 (없으면 서버 message fallback)
var CART_ERR_I18N = {
  NOT_OWNER: 'cartErrNotOwner', NOT_MALSO: 'cartErrNotMalso', ALREADY_IN_CART: 'cartErrAlready',
  OVER_CAPACITY: 'cartErrOverCapacity', EMPTY_CART: 'cartErrEmpty', CART_LOCKED: 'cartErrLocked',
  NO_COUNTRY: 'cartErrNoCountry'
};

function won(n) { return (Number(n) || 0).toLocaleString('en-US'); }

// 적재율 바
function UtilizationBar(props) {
  var u = props.u, t = props.t;
  var pct = Math.min(100, u.percent || 0);
  var color = u.isFull ? '#ef4444' : (pct >= 80 ? '#f59e0b' : '#10b981');
  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#64748b', marginBottom: 4 }}>
        <span>{t('cartUtilization')} · {u.totalCbm} / {u.capacityCbm} CBM</span>
        <span style={{ fontWeight: 700, color: color }}>{u.percent}%</span>
      </div>
      <div style={{ height: 10, background: '#f1f5f9', borderRadius: 6, overflow: 'hidden' }}>
        <div style={{ width: pct + '%', height: '100%', background: color, transition: 'width .3s' }} />
      </div>
      <div style={{ fontSize: 11, color: u.isFull ? '#ef4444' : '#94a3b8', marginTop: 4 }}>
        {u.isFull
          ? '⚠ ' + t('cartFull')
          : t('cartMoreCars', { n: u.estMoreCars })}
      </div>
    </div>
  );
}

function AuctionBuyerCart() {
  var navigate = useNavigate();
  var t = useT();
  var [carts, setCarts] = useState([]);
  var [eligible, setEligible] = useState([]);
  var [loading, setLoading] = useState(true);
  var [error, setError] = useState('');
  var [busy, setBusy] = useState(false);

  var fetchJson = useCallback(function(path, opts) {
    return authBuyerFetch(API + '/buyer-app/carts' + path, opts || {})
      .then(function(r) {
        if (isUnauthorized(r)) { handleUnauthorized(navigate); throw new Error('unauthorized'); }
        return r.json();
      });
  }, [navigate]);

  var load = useCallback(function() {
    setLoading(true);
    setError('');
    // 카트 목록 → 각 카트 상세(품목 포함) 병합 + eligible
    Promise.all([
      fetchJson('/', {}),
      fetchJson('/eligible-cars', {})
    ]).then(function(res) {
      var listRes = res[0], eligRes = res[1];
      if (eligRes && eligRes.success) setEligible(eligRes.data || []);
      var summaries = (listRes && listRes.success && listRes.data) ? listRes.data : [];
      // 각 카트 상세(품목) 로드
      return Promise.all(summaries.map(function(s) {
        return fetchJson('/' + s.cart.id, {}).then(function(d) {
          return (d && d.success && d.data) ? { cart: d.data.cart, utilization: d.data.utilization } : s;
        });
      }));
    }).then(function(detailed) {
      setCarts(detailed);
      setLoading(false);
    }).catch(function(e) {
      if (e && e.message === 'unauthorized') return;
      setError(t('commonNetwork'));
      setLoading(false);
    });
  }, [fetchJson, t]);

  useEffect(function() { load(); }, [load]);

  function mapErr(d) {
    if (d && d.error && CART_ERR_I18N[d.error]) return t(CART_ERR_I18N[d.error]);
    return displayApiError(d, t, 'commonError');
  }

  function addToCart(carId) {
    if (busy) return;
    setBusy(true);
    fetchJson('/items', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ carId: carId }) })
      .then(function(d) {
        if (d && d.success) { showToast(t('cartAddDone'), { variant: 'success' }); load(); }
        else showToast(mapErr(d), { variant: 'error' });
        setBusy(false);
      })
      .catch(function(e) { if (e && e.message === 'unauthorized') return; showToast(t('commonNetwork'), { variant: 'error' }); setBusy(false); });
  }

  function removeItem(cartId, carId) {
    if (busy) return;
    setBusy(true);
    fetchJson('/' + cartId + '/items/' + carId, { method: 'DELETE' })
      .then(function(d) {
        if (d && d.success) load();
        else { showToast(mapErr(d), { variant: 'error' }); setBusy(false); }
      })
      .catch(function(e) { if (e && e.message === 'unauthorized') return; showToast(t('commonNetwork'), { variant: 'error' }); setBusy(false); });
  }

  function lockCart(cartId) {
    confirm({ message: t('cartLockConfirm') }).then(function(ok) {
      if (!ok) return;
      setBusy(true);
      fetchJson('/' + cartId + '/lock', { method: 'POST' })
        .then(function(d) {
          if (d && d.success) { showToast(t('cartLockDone'), { variant: 'success' }); load(); }
          else { showToast(mapErr(d), { variant: 'error' }); setBusy(false); }
        })
        .catch(function(e) { if (e && e.message === 'unauthorized') return; showToast(t('commonNetwork'), { variant: 'error' }); setBusy(false); });
    });
  }

  return (
    <AuctionBuyerLayout activeKey="cart">
      <div className="buyer-page-container" style={{ maxWidth: 1000, margin: '0 auto', padding: '32px 32px 80px' }}>
        <h2 style={{ fontSize: 22, fontWeight: 800, color: '#0f172a' }}>📦 {t('cartTitle')}</h2>
        <p style={{ fontSize: 13, color: '#64748b', marginTop: 4, marginBottom: 24 }}>{t('cartIntro')}</p>

        {error && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: 14, marginBottom: 20, color: '#991b1b', fontSize: 14 }}>⚠ {error}</div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>{t('commonLoading')}</div>
        ) : (
          <>
            {/* 내 컨테이너 카트 */}
            <div style={{ fontSize: 13, fontWeight: 700, color: '#475569', marginBottom: 12 }}>{t('cartMyCarts')}</div>
            {carts.length === 0 ? (
              <EmptyState icon="📦" title={t('cartEmpty')} hint={t('cartEmptyHint')} minHeight={160} />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 32 }}>
                {carts.map(function(entry) {
                  var cart = entry.cart, u = entry.utilization || {};
                  var items = cart.items || [];
                  var isDraft = cart.status === 'draft';
                  return (
                    <div key={cart.id} style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 14, padding: 20 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                        <div style={{ fontSize: 15, fontWeight: 800, color: '#0f172a' }}>
                          🌍 {t('cartDestCountry')}: {cart.dest_country || '-'}
                          <span style={{ marginLeft: 10, fontSize: 12, fontWeight: 700, color: '#fff', background: CART_STATUS_COLOR[cart.status] || '#94a3b8', padding: '2px 10px', borderRadius: 10 }}>
                            {t(CART_STATUS_I18N[cart.status] || 'cartStatusDraft')}
                          </span>
                        </div>
                        <div style={{ fontSize: 13, color: '#64748b' }}>{t('cartCount', { n: items.length })}</div>
                      </div>

                      <UtilizationBar u={u} t={t} />

                      {/* 품목 */}
                      <div style={{ marginTop: 14, borderTop: '1px solid #f1f5f9', paddingTop: 8 }}>
                        {items.length === 0 ? (
                          <div style={{ fontSize: 13, color: '#94a3b8', padding: '8px 0' }}>{t('cartNoItems')}</div>
                        ) : items.map(function(it) {
                          return (
                            <div key={it.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f8fafc', fontSize: 14 }}>
                              <div>
                                <span style={{ fontWeight: 700, color: '#0f172a' }}>{it.carType || '-'}</span>
                                <span style={{ marginLeft: 8, fontSize: 12, color: '#94a3b8' }}>{it.carNo || ''} · {it.status || ''}</span>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                <span style={{ fontSize: 13, color: '#475569' }}>₩{won(it.buyPrice)}</span>
                                {isDraft && (
                                  <button type="button" disabled={busy} onClick={function(){ removeItem(cart.id, it.car_id); }}
                                    style={{ border: 'none', background: 'none', color: '#ef4444', fontSize: 13, fontWeight: 600, cursor: busy ? 'not-allowed' : 'pointer' }}>
                                    {t('cartRemove')}
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* 잠금 (draft + 품목 있음 + 용적 초과 아님) */}
                      {isDraft && items.length > 0 && (
                        <button type="button" disabled={busy || u.isFull} onClick={function(){ lockCart(cart.id); }}
                          style={{ marginTop: 14, width: '100%', height: 46, border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 700,
                            background: u.isFull ? '#e2e8f0' : 'linear-gradient(135deg,#1B3A5C 0%,#3b82f6 100%)',
                            color: u.isFull ? '#94a3b8' : 'white', cursor: (busy || u.isFull) ? 'not-allowed' : 'pointer' }}>
                          🔒 {t('cartLock')}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* 담을 수 있는 차량 */}
            <div style={{ fontSize: 13, fontWeight: 700, color: '#475569', marginBottom: 12 }}>{t('cartEligibleTitle')}</div>
            {eligible.length === 0 ? (
              <EmptyState icon="🚗" title={t('cartEligibleEmpty')} hint={t('cartEligibleEmptyHint')} minHeight={160} />
            ) : (
              <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 14, overflow: 'hidden' }}>
                {eligible.map(function(car) {
                  return (
                    <div key={car.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 18px', borderBottom: '1px solid #f1f5f9' }}>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{car.carType || '-'}</div>
                        <div style={{ fontSize: 12, color: '#94a3b8' }}>{car.carNo || ''} · {car.status || ''} · ₩{won(car.buyPrice)}</div>
                      </div>
                      <button type="button" disabled={busy} onClick={function(){ addToCart(car.id); }}
                        style={{ height: 36, padding: '0 16px', border: 'none', borderRadius: 8, background: '#1B3A5C', color: 'white', fontSize: 13, fontWeight: 700, cursor: busy ? 'not-allowed' : 'pointer' }}>
                        + {t('cartAdd')}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </AuctionBuyerLayout>
  );
}

export default AuctionBuyerCart;
